import moment from "moment-timezone";
import { getAuthUser } from "../../../../hooks/useAuthUser";
import { formatCoupleOptionLabel } from "../../../../utils/coupleLabels";
import {
  SUPPORT_TYPES,
  type CoupleRow,
  type OversightContactKind,
  type ParishRow,
  type Season,
  type SupportPerson,
  type SupportTypeCode,
} from "./projectedRetreatConstants";

const NY_TZ = "America/New_York";

const SEASON_START_MONTH: Record<Season, number> = {
  Winter: 11,
  Spring: 2,
  Summer: 5,
  Fall: 8,
};

export function seasonFromMonth(monthIndex: number): Season {
  if (monthIndex === 11 || monthIndex <= 1) return "Winter";
  if (monthIndex >= 2 && monthIndex <= 4) return "Spring";
  if (monthIndex >= 5 && monthIndex <= 7) return "Summer";
  return "Fall";
}

export function toLocalNoon(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    12,
    0,
    0,
  );
}

export function calendarDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Local noon Date for the America/New_York calendar day of `value`. */
export function parseNyCalendarDate(
  value?: string | Date | null,
): Date | null {
  if (value == null || value === "") return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [y, m, d] = value.trim().split("-").map(Number);
    return new Date(y, m - 1, d, 12, 0, 0);
  }
  const ny = moment(value).tz(NY_TZ);
  if (!ny.isValid()) return null;
  return new Date(ny.year(), ny.month(), ny.date(), 12, 0, 0);
}

export function seasonStartMonth(season: Season): number {
  return SEASON_START_MONTH[season];
}

/** Winter Y is December Y through February Y+1; other seasons stay in year Y. */
export function seasonDateRange(
  season: Season,
  year: number,
): { start: Date; end: Date } {
  const y = Number(year);
  if (season === "Winter") {
    return {
      start: new Date(y, 11, 1, 0, 0, 0),
      end: new Date(y + 1, 2, 0, 23, 59, 59, 999),
    };
  }
  const startMonth = seasonStartMonth(season);
  return {
    start: new Date(y, startMonth, 1, 0, 0, 0),
    end: new Date(y, startMonth + 3, 0, 23, 59, 59, 999),
  };
}

export function isDateInSeason(
  date: Date,
  season: Season,
  year: number,
): boolean {
  const { start, end } = seasonDateRange(season, year);
  const t = toLocalNoon(date).getTime();
  return t >= start.getTime() && t <= end.getTime();
}

/** Jan/Feb belong to Winter of the previous calendar year. */
export function seasonYearFromDate(date: Date): number {
  const month = date.getMonth();
  const year = date.getFullYear();
  if (month === 0 || month === 1) return year - 1;
  return year;
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function formatWeekendRange(friday: Date, sunday: Date): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  return `${fmt(friday)} – ${fmt(sunday)}`;
}

export function formatCurrency(amount: number): string {
  return amount.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

export function parishDisplayName(p?: ParishRow | null) {
  return (p?.parish || p?.name || "").trim();
}

export function parishAddressLine(p?: ParishRow | null) {
  if (!p) return "";
  const zip = p.zip || p.postalCode || "";
  const cityState = [p.city, p.state].filter(Boolean).join(", ");
  return [p.address, cityState, zip].filter(Boolean).join(", ");
}

export function coupleShortName(c?: CoupleRow | null) {
  if (!c) return "";
  const his = (c.hisFirstName || "").trim();
  const her = (c.herFirstName || "").trim();
  const last = (c.hisLastName || c.herLastName || "").trim();
  if (his && her) return `${his} & ${her}${last ? ` ${last}` : ""}`;
  return formatCoupleOptionLabel(c) || `Couple #${c.id}`;
}

export function coupleEmails(c?: CoupleRow | null) {
  if (!c) return "";
  return [c.hisEmail, c.herEmail]
    .map((e) => (e || "").trim())
    .filter(Boolean)
    .join(" · ");
}

export function contactOptionValue(kind: OversightContactKind, id: number) {
  return `${kind}:${Number(id)}`;
}

export function parseContactOptionValue(
  value?: string | null,
): { kind: OversightContactKind; id: number } | null {
  if (!value) return null;
  const idx = value.indexOf(":");
  if (idx <= 0) return null;
  const kind = value.slice(0, idx);
  const id = Number(value.slice(idx + 1));
  if ((kind !== "user" && kind !== "couple") || !Number.isFinite(id) || !id) {
    return null;
  }
  return { kind, id };
}

export function contactKindForSupportType(
  type: SupportTypeCode | string,
): OversightContactKind {
  return type === SUPPORT_TYPES.EXPERIENCED_COUPLE ? "couple" : "user";
}

export function formatPersonName(p?: {
  firstName?: string;
  lastName?: string;
  email?: string;
} | null) {
  if (!p) return "";
  const name = [p.firstName, p.lastName]
    .map((s) => (s || "").trim())
    .filter(Boolean)
    .join(" ");
  return name || (p.email || "").trim();
}

export function formatPersonOptionLabel(p: {
  firstName?: string;
  lastName?: string;
  email?: string;
}) {
  const name = formatPersonName(p);
  const email = (p.email || "").trim();
  if (name && email && name !== email) return `${name} (${email})`;
  return name || email || "Unknown";
}

export function flattenSteeringMembers(data: unknown): SupportPerson[] {
  if (!data || typeof data !== "object") return [];
  const out: SupportPerson[] = [];
  const seen = new Set<string>();
  for (const group of Object.values(data as Record<string, any>)) {
    const parishId = Number(group?.parishId || 0);
    const users = Array.isArray(group?.users) ? group.users : [];
    for (const u of users) {
      const id = Number(u?.userId || u?.id || 0);
      if (!id) continue;
      const key = `${id}:${parishId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        id,
        firstName: u.firstName || "",
        lastName: u.lastName || "",
        email: u.email || u.eMail || "",
        parishId,
      });
    }
  }
  return out;
}

export function flattenSuperAdmins(admins: unknown): SupportPerson[] {
  if (!Array.isArray(admins)) return [];
  return admins
    .filter((a) => a && Number(a.isDisabled) !== 1)
    .map((a) => ({
      id: Number(a.id),
      firstName: a.firstName || "",
      lastName: a.lastName || "",
      email: a.email || "",
    }))
    .filter((a) => a.id);
}

export function oversightContactHelper(
  supportType: SupportTypeCode | string,
  parishId: number | "",
) {
  if (supportType === SUPPORT_TYPES.STEERING_COMMITTEE) {
    if (!parishId) return "Select a parish first to see steering members.";
    return "Steering committee members for the selected parish.";
  }
  if (supportType === SUPPORT_TYPES.EXECUTIVE_COMMITTEE) {
    return "All super admins — add under Executive Committee.";
  }
  return "Full couple list — add under Mentor Couple.";
}

export function oversightContactPlaceholder(
  supportType: SupportTypeCode | string,
) {
  if (supportType === SUPPORT_TYPES.STEERING_COMMITTEE) {
    return "Search steering members…";
  }
  if (supportType === SUPPORT_TYPES.EXECUTIVE_COMMITTEE) {
    return "Search super admins…";
  }
  return "Search couples by name or email…";
}

export function oversightSectionSubtitle(
  supportType: SupportTypeCode | string,
) {
  if (supportType === SUPPORT_TYPES.STEERING_COMMITTEE) {
    return "Choose Steering Committee, then add planning contacts from that parish's steering members.";
  }
  if (supportType === SUPPORT_TYPES.EXECUTIVE_COMMITTEE) {
    return "Choose Executive Committee, then add planning contacts from all super admins.";
  }
  return "Choose Mentor Couple, then add planning contacts from the full couple list.";
}

export function oversightOptionsForType(retreatType: string): SupportTypeCode[] {
  if (retreatType === "Archbishop Outreach Retreat") {
    return [SUPPORT_TYPES.EXECUTIVE_COMMITTEE];
  }
  if (retreatType === "New Parish / First Retreat") {
    return [
      SUPPORT_TYPES.EXECUTIVE_COMMITTEE,
      SUPPORT_TYPES.EXPERIENCED_COUPLE,
    ];
  }
  return [
    SUPPORT_TYPES.STEERING_COMMITTEE,
    SUPPORT_TYPES.EXECUTIVE_COMMITTEE,
    SUPPORT_TYPES.EXPERIENCED_COUPLE,
  ];
}

export function actorPayload() {
  const user = getAuthUser<any>();
  return {
    actorName:
      user?.userName ||
      [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
      "User",
    actorRole: user?.userRole || "External",
    actorUserId: user?.id || user?.userId || null,
  };
}

function reportPdfFileName(fileStem?: string) {
  const safe = String(fileStem || "Projected-Retreat-Report")
    .replace(/[^\w\-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `${safe || "Projected-Retreat-Report"}.pdf`;
}

const REPORT_STYLE_PROPS = [
  "color",
  "backgroundColor",
  "backgroundImage",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "letterSpacing",
  "lineHeight",
  "textAlign",
  "textTransform",
  "whiteSpace",
  "wordBreak",
  "overflowWrap",
  "borderTopColor",
  "borderRightColor",
  "borderBottomColor",
  "borderLeftColor",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "borderTopStyle",
  "borderRightStyle",
  "borderBottomStyle",
  "borderLeftStyle",
  "borderTopLeftRadius",
  "borderTopRightRadius",
  "borderBottomRightRadius",
  "borderBottomLeftRadius",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "display",
  "flexDirection",
  "flexWrap",
  "justifyContent",
  "alignItems",
  "alignSelf",
  "gap",
  "rowGap",
  "columnGap",
  "gridTemplateColumns",
  "gridTemplateRows",
  "boxSizing",
  "opacity",
  "visibility",
  "overflow",
  "overflowX",
  "overflowY",
] as const;

function inlineComputedPaintStyles(from: Element, to: Element) {
  const fromEl = from as HTMLElement;
  const toEl = to as HTMLElement;
  const cs = window.getComputedStyle(fromEl);
  for (const prop of REPORT_STYLE_PROPS) {
    toEl.style.setProperty(prop, cs.getPropertyValue(prop));
  }
  const weight = Number.parseInt(cs.fontWeight, 10);
  if (Number.isFinite(weight) && weight > 800) {
    toEl.style.fontWeight = "800";
  }
  toEl.style.transform = "none";
  toEl.style.filter = "none";
  toEl.style.boxShadow = "none";
  toEl.style.textShadow = "none";
  toEl.style.setProperty("-webkit-text-fill-color", cs.color);
  const fromChildren = fromEl.children;
  const toChildren = toEl.children;
  const count = Math.min(fromChildren.length, toChildren.length);
  for (let i = 0; i < count; i += 1) {
    inlineComputedPaintStyles(fromChildren[i], toChildren[i]);
  }
}

async function buildProjectedRetreatReportPdf() {
  const source = document.getElementById("projected-retreat-report");
  if (!source) return null;

  const html2canvas = (await import("html2canvas")).default;
  const jsPDF = (await import("jspdf")).default;

  const pageWidthPx = Math.round(8.5 * 96);
  const holder = document.createElement("div");
  holder.setAttribute("data-projected-report-capture", "true");
  holder.style.cssText = [
    "position:fixed",
    "left:0",
    "top:0",
    `width:${pageWidthPx}px`,
    "background:#ffffff",
    "z-index:2147483646",
    "opacity:1",
    "pointer-events:none",
  ].join(";");

  const clone = source.cloneNode(true) as HTMLElement;
  clone.id = "projected-retreat-report-capture";
  clone.style.width = `${pageWidthPx}px`;
  clone.style.maxWidth = `${pageWidthPx}px`;
  clone.style.margin = "0";
  clone.style.boxShadow = "none";
  clone.style.position = "static";
  clone.style.transform = "none";
  clone.style.background = "#ffffff";
  clone.style.overflow = "visible";
  holder.appendChild(clone);
  document.body.appendChild(holder);
  inlineComputedPaintStyles(source, clone);

  try {
    if (document.fonts?.ready) await document.fonts.ready;
    await new Promise((resolve) => window.setTimeout(resolve, 120));

    const canvas = await html2canvas(clone, {
      scale: 3,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      foreignObjectRendering: false,
      scrollX: 0,
      scrollY: 0,
      width: clone.scrollWidth,
      height: clone.scrollHeight,
      windowWidth: clone.scrollWidth,
      windowHeight: clone.scrollHeight,
    });

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "in",
      format: "letter",
      compress: false,
    });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL("image/png", 1);
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    if (imgHeight <= pageHeight + 0.02) {
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, undefined, "NONE");
    } else {
      let offset = 0;
      while (offset < imgHeight) {
        if (offset > 0) pdf.addPage();
        pdf.addImage(
          imgData,
          "PNG",
          0,
          -offset,
          imgWidth,
          imgHeight,
          undefined,
          "NONE",
        );
        offset += pageHeight;
      }
    }
    return pdf;
  } finally {
    holder.remove();
  }
}

function openPdfForPrint(pdf: {
  autoPrint: () => void;
  output: (type: "bloburl") => string;
}) {
  pdf.autoPrint();
  const blobUrl = pdf.output("bloburl");
  const iframe = document.createElement("iframe");
  iframe.setAttribute("data-projected-report-print-frame", "true");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  iframe.src = blobUrl;
  document.body.appendChild(iframe);
  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } catch {
      window.open(blobUrl, "_blank");
    }
    window.setTimeout(() => {
      iframe.remove();
      URL.revokeObjectURL(blobUrl);
    }, 60_000);
  };
  iframe.onload = triggerPrint;
  window.setTimeout(triggerPrint, 900);
}

export async function exportProjectedRetreatReport(
  fileStem?: string,
  mode: "print" | "save" = "save",
) {
  const pdf = await buildProjectedRetreatReportPdf();
  if (!pdf) return;
  if (mode === "print") {
    openPdfForPrint(pdf);
    return;
  }
  pdf.save(reportPdfFileName(fileStem));
}

/** @deprecated Use exportProjectedRetreatReport(fileStem, "save") */
export async function printProjectedRetreatReport(fileStem?: string) {
  return exportProjectedRetreatReport(fileStem, "save");
}
